import type { Bounds, LatLng, MapCamera } from "@oshimap/domain";
import type { MapMarkerData } from "./types";
import type { Cluster, Single } from "@oshimap/utils";

export type MapClusterNode = Cluster<MapMarkerData & { id: string }> | Single<MapMarkerData & { id: string }>;

/**
 * 19_NATIVE_APP_STRATEGY §5.
 * ⛔ Domain / 일반 UI 코드는 이 인터페이스 밖의 SDK 를 직접 호출하지 않는다 (FR-MAP-10).
 * Legacy `services/map/map-types.ts` 의 MapProvider 를 승계하고 MapCamera 를 추가했다.
 */
export interface BasemapProvider {
  readonly name: string;
  initialize(container: unknown, center: LatLng, zoom: number): Promise<void>;
  destroy(): void;
  setCenter(point: LatLng, zoom?: number): void;
  getCamera(): MapCamera | null;
  fitBounds(bounds: Bounds): void;
  getBounds(): Bounds | null;
  setUserLocation(point: LatLng | null): void;
  setMarkers(markers: readonly MapMarkerData[], onSelect: (m: MapMarkerData) => void): void;
  updateSelectedMarker(key: string | null): void;
  setMarkerClustering(enabled: boolean): void;
  setClusterClickHandler(handler: (c: MapClusterNode) => void): void;
  removeMarker(id: string): void;
  clearMarkers(): void;
  onIdle(listener: () => void): () => void;
  onProviderError(listener: (message: string) => void): () => void;
}

/**
 * **바닥 지도(Basemap) Provider.** OD-003 ACCEPTED (2026-09-08, 사람 결정).
 *
 * 기본은 **Google Maps Platform** 이다 — Web · iOS · Android 전부.
 * `naver` / `kakao` / `apple` 은 union 에 남겨 두지만 **채택된 것이 아니다.**
 * NAVER의 한국 Route 정책은 별도 capability 계약에서 관리한다.
 */
export type BasemapProviderKind = "google" | "naver" | "kakao" | "apple" | "mock";

/**
 * **데이터 Provider.** 바닥 지도가 아니다.
 *
 * VWorld 는 Primary Basemap Provider 가 **아니라** 대한민국 공간정보를 위한
 * 선택적 데이터 출처다. 두 개념을 한 union 에 섞으면 "VWorld 로 지도를 그린다"
 * 는 오해가 코드 차원에서 가능해진다 — 그래서 타입을 나눈다.
 */
export type MapDataProviderKind = "vworld";

const KOREA = { north: 38.7, south: 33.0, east: 131.9, west: 124.5 };
export function isPointInKorea(p: LatLng): boolean {
  return p.latitude >= KOREA.south && p.latitude <= KOREA.north
    && p.longitude >= KOREA.west && p.longitude <= KOREA.east;
}

/*
 * `preferredProviderForPoint` / `dataProvidersForPoint` 는 지웠습니다.
 * capability 계층(`capabilityPolicyForLocation` + `selectCapabilityProvider`)이
 * 같은 일을 더 정확하게 하고 있었고, 규칙이 두 곳에 있으면 반드시 어긋납니다.
 * OD-003 결정(Google 우선 · VWorld 는 basemap 아님)은 capabilities 테스트가
 * 그대로 지킵니다.
 */
